import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTheme, ActionSheet, useUpdateModal } from '@zydalabs/storefront-components';
import dynamic from 'next/dynamic';
import Cookies from 'js-cookie';
import useTranslation from 'next-translate/useTranslation';
import { Typography, Button } from '@zydalabs/zac-react';

import { StyledActionSheetWrapper, StyledPageWrapper } from 'common/styles';
import {
  FULFILLMENT_MODES,
  ORDER_TIME_TYPES,
  PAYMENT_METHODS,
  URLS,
  PAYMENT_GATEWAYS,
  FACEBOOK_PIXEL_EVENTS,
  DATA_LAYER_CONTENT_TYPE,
  SNAPCHAT_EVENTS,
  TIKTOK_EVENTS,
} from 'common/constants';
import {
  useGlobalOrderTypeAndTime,
  usePersistedCartData,
  usePersistedCountryDetails,
  usePersistedOrderMode,
  usePersistedPaymentCard,
  usePersistedRecipientInfo,
  usePersistedUserAddressId,
  usePersistedFulfillmentTimeAndType,
  usePersistedUserCarId,
  usePersistedWalletUsed,
  usePersistedUserUmbrellaData,
  usePersistedBranchId,
  usePersistedSupportedDeliveryZone,
  usePersistedSelectedCoordinates,
  useUserData,
  usePersistedOperatorId,
  usePersistedReferenceId,
} from 'common/hooks';
import {
  useCartDetails,
  useStorePaymentDetails,
  useShowAddress,
  useVoucherDetails,
  useStoreSettings,
  useUserInfo,
  useCheckSupportedZoneCallback,
  useCreateFacebookCapi,
} from 'service/hooks';
import { useUpdateCart } from 'modules/productsModule/hooks';
import { CustomActionsRegistry, RenderingRegistry } from 'common/lib/registries';
import { CustomActionsEnum } from 'common/enums';
import { Address, AddressBook, Car, DeliveryZoneMs, PaymentMethod } from 'service/types/generated';
import UserBookList from 'modules/userModule/components/UserBookList';
import GiftOrderingSection from 'modules/checkoutModule/components/GiftOrderingSection';
import {
  checkIfTimeNowInBetweenAsapTimeUsingDzms,
  compareDeliveryZoneInCartCacheResponse,
  getOpenAndCloseHours,
  isBranchBusyAtDateTime,
  itemNotPublishedInBranchModal,
  setUserInfoCookies,
  useRemoveCartItemsOutOfSync,
} from 'modules/checkoutModule/utils';
import { changeRoute, localizeCurrency, logger, replaceRoute, trackFacebookEvent, trackTiktokEvents } from 'common/utils';
import PaymentMethodsSection from 'modules/checkoutModule/components/PaymentMethodsSection';
import ServiceConfigs from 'service/config';
import { trackSnapchatEvents } from 'common/utils/trackSnapchatEvents';
import OpenStreetMap from 'common/components/OpenStreetMap';
import { StyledEditLocationContainer, StyledMapWrapper } from 'modules/userModule/screens/AddressPage/styles';
import { CoordsType } from 'common/types';
import logCloudWatch from 'common/utils/logCloudWatch';
import { DateTime } from 'luxon';
import { getOpeningHoursByDay, isStoreClosedAtDate } from 'modules/locationsModule/utils';
import {
  getEstimatedTimeText,
  getNextAvailableWorkingDayInterval,
} from 'modules/locationsModule/screens/EstimatedTimePage/utils';
import { useDecideUserBook, useHandleCheckout, useHandleSendCartLink } from '../../hooks';
import { getAvailabilityTypeAttributes } from '../../../productsModule/utils';
import { StyledButtonWrapper, StyledDeliveryInfoUpdatedContainer } from './style';
import { useFulfillmentSettings } from '../../../../contexts';

const CheckoutLoadingComponent = dynamic(() => import('./loading'));
const StoreFrontHeaderComponent = dynamic(() => import('common/components/StoreFrontHeader'));
const CheckoutUserInfoComponent = dynamic(() => import('modules/checkoutModule/components/CheckoutUserInfo'));
const ReceiptComponent = dynamic(() => import('modules/productsModule/components/Receipt'));

const CheckoutPage: React.FC = () => {
  const { t, lang } = useTranslation('checkout');

  const theme: any = useTheme();

  const useDeliveryzonesMs = ServiceConfigs?.getUseDeliveryzonesMs();
  const storeId = ServiceConfigs?.getStoreId();

  const { userDataBackEnd, capiEnabled, tiktokData } = useUserData();
  const createCapiEvent = useCreateFacebookCapi();

  const { data: paymentData, isLoading: isPaymentLoading, mutate: mutatePaymentMethods } = useStorePaymentDetails();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const [isWalletUsed] = usePersistedWalletUsed();
  const [operatorId] = usePersistedOperatorId();
  const [paymentCard, , removePaymentCard] = usePersistedPaymentCard();
  const { data: userInfo } = useUserInfo();
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const [mode] = usePersistedOrderMode();
  const [branchId] = usePersistedBranchId();
  const [persistedUserAddressId] = usePersistedUserAddressId();
  const [persistedUserCarId] = usePersistedUserCarId();
  const [cartData] = usePersistedCartData();
  const [persistedRecipientInfo] = usePersistedRecipientInfo();
  const [persistedFulfillmentTimeAndType] = usePersistedFulfillmentTimeAndType();
  const [globalOrderTypeAndTime] = useGlobalOrderTypeAndTime();
  const {
    fulfillmentTimeType,
    scheduleSlotInterval,
    estimatedTimeText,
    isScheduledEnabled,
    actionButtonText,
    isLoading: isCalculating,
    recalculate,
  } = globalOrderTypeAndTime || {};
  const [persistedUmbrellaData] = usePersistedUserUmbrellaData();
  const [supportedDeliveryZone, setSupportedDeliveryZone] = usePersistedSupportedDeliveryZone();
  const [selectedCoords] = usePersistedSelectedCoordinates();
  const [hasReferenceId] = usePersistedReferenceId();
  const { data: storeSettings } = useStoreSettings();
  const { area, branch: branchDetails, isLoading: isFulfillmentSettingsLoading } = useFulfillmentSettings();
  const { setting, scheduleInfo } = storeSettings || {};
  const { busyMode, busyFrom, busyUntil, openingHours: branchOpeningHours } = branchDetails || {};
  const isBranchBusy = isBranchBusyAtDateTime(DateTime.now(), mode, busyMode, busyFrom, busyUntil);

  const { currency, id: countryId, timeZone } = persistedCountryDetails || {};
  const isDelivery = mode === FULFILLMENT_MODES.DELIVERY;
  const isCarPickup = mode === FULFILLMENT_MODES.CAR_PICKUP;
  const isOrderScheduled = fulfillmentTimeType === ORDER_TIME_TYPES.SCHEDULE;
  const openingHours = isDelivery ? supportedDeliveryZone?.openingHours : branchOpeningHours;
  const nextOpeningDateTime = openingHours && getNextAvailableWorkingDayInterval(getOpeningHoursByDay(openingHours), 7)?.start;

  const { data: userBookData, mutate: mutateUserBook } = useDecideUserBook({
    isDelivery,
    isCarPickup,
    areaId: area?.id,
  });
  const { deliveryVatInclusive } = setting || {};

  const userBook: Array<Car | Address> = isDelivery
    ? ((userBookData as AddressBook)?.addresses as Array<Address>)
    : (userBookData as Array<Car>);

  const {
    data: cartDetails,
    isLoading: isCartDetailsLoading,
    mutate: mutateCartDetails,
  } = useCartDetails({ ...cartData, config: { revalidateOnMount: true } });
  const updateCart = useUpdateCart();

  const {
    data: userAddress,
    isLoading: isLoadingShowAddress,
    mutate: mutateShowAddress,
  } = useShowAddress({
    addressId: isDelivery && persistedUserAddressId,
  });

  const isManualOrder = RenderingRegistry.getIsManualOrder();

  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState<boolean>();
  const [checkingSupportedZone, setCheckingSupportedZone] = useState<boolean>(
    useDeliveryzonesMs && isDelivery && !isManualOrder,
  );
  const [hasDeliveryZoneChanged, setHasDeliveryZoneChanged] = useState<boolean>(false);
  const [isCartItemUnavailable, setIsCartItemUnavailable] = useState<boolean>(false);

  const estimatedTimeDetails = estimatedTimeText?.[lang] || '';

  const {
    cartItems,
    areaId,
    subtotal,
    discountedAmount,
    voucherCode,
    tax,
    total,
    taxInclusive,
    taxPercentage,
    deliveryFee,
    serviceFee,
    voucherAmount,
    possibleCashbackAmount,
    cartItemsCount,
    voucherDiscountedItems,
    deliveryTime,
    minPreprationTime,
  } = cartDetails || {};

  const checkSupportedZoneCallback = useCheckSupportedZoneCallback();
  const checkout = useHandleCheckout();
  const sendCartLink = useHandleSendCartLink();
  const updateModal = useUpdateModal();
  const isCash = selectedPaymentMethod?.name === PAYMENT_METHODS.CASH;
  const isCreditCard = selectedPaymentMethod?.name === PAYMENT_METHODS.CREDIT_CARD;
  const isWalletAndCash = isWalletUsed && isCash;

  const { data: voucherDetails } = useVoucherDetails({ code: voucherCode });
  const { minSubtotalAmount: voucherMinSubtotal = 0 } = voucherDetails || {};

  useEffect(() => {
    const hasUnavailableCartItem = cartItems?.some(cartItem => {
      const { variant, menuItem } = cartItem;
      const { stockData } = variant;
      const { unavailable, trackingType, stockCount } = stockData || {};
      const { publishedBranchIds } = menuItem || {};
      const publishedInCurrentBranch = !!publishedBranchIds && publishedBranchIds.includes(branchId);

      if (!publishedInCurrentBranch) itemNotPublishedInBranchModal(updateModal, t);

      const { unavailabilityReason } = getAvailabilityTypeAttributes({
        productAvailabilityMode: menuItem?.fulfillmentMode,
        selectedMode: mode,
        unavailable,
        stockCount,
        trackingType,
        publishedInCurrentBranch,
      });
      return !!unavailabilityReason;
    });
    setIsCartItemUnavailable(hasUnavailableCartItem);
  }, [cartItems, branchId]);

  const isAddressInvalid = useDeliveryzonesMs
    ? !userAddress?.covered
    : userAddress?.area?.id.toString() !== areaId?.toString() || !userAddress?.covered;
  const isDeliveryInvalid = isDelivery && isAddressInvalid;
  const isCarPickupInvalid = isCarPickup && !persistedUserCarId;
  const isVoucherMinAmountInValid = voucherCode && voucherMinSubtotal > subtotal;
  const minOrderValue = useDeliveryzonesMs ? userAddress?.deliveryZone?.minimumOrder : area?.deliveryZone?.minimumOrder;
  const effectiveSubtotal = subtotal - (discountedAmount || 0);
  const isMinOrderInValid = minOrderValue && effectiveSubtotal < minOrderValue;
  const isOnlinePaymentWithManualOrder = isManualOrder && selectedPaymentMethod?.id;
  const isDisabledUntilOrderTimeIsChosen = !fulfillmentTimeType && actionButtonText;
  const walletBalance = userInfo?.walletBalance;
  const isWalletBalanceCoverOrderAmount = isWalletUsed && walletBalance >= total;
  const isWalletAndCreditCard = isWalletUsed && isCreditCard && !isWalletBalanceCoverOrderAmount;
  const totalOrderProcessingTime = isDelivery ? minPreprationTime + deliveryTime : minPreprationTime;
  const isClosed =
    openingHours && timeZone
      ? isStoreClosedAtDate(openingHours, timeZone, DateTime.now().plus({ minutes: totalOrderProcessingTime || 0 }))
      : false;

  const placeOrderText = `${t('placeOrder')}`;

  const { removeItemsOutOfSync, isLoading: isRemovingCartItemsLoading } = useRemoveCartItemsOutOfSync({
    mutateCartDetails,
    cartItems,
  });

  const branchCoordinates: CoordsType = { lat: branchDetails?.lat, lng: branchDetails?.lng };

  const getPlaceOrderButtonText = () => {
    if (!isScheduledEnabled && isClosed) {
      if (nextOpeningDateTime) {
        const timeText = getEstimatedTimeText(mode, nextOpeningDateTime);
        return `${t('orderingStartsAt')} ${timeText[lang]}`;
      }
      return `${t('storeClosed')}`;
    }
    if (!isScheduledEnabled && isBranchBusy) {
      return `${t('busyBranch')}`;
    }
    if (!selectedPaymentMethod) {
      return `${t('selectPaymentMethodText')}`;
    }
    if (isMinOrderInValid && minOrderValue >= voucherMinSubtotal && currency)
      return `${t('minimumOrderAmount', {
        amount: localizeCurrency(lang, { amount: minOrderValue, currency: currency.title_en }),
      })}`;
    if (isVoucherMinAmountInValid && voucherMinSubtotal && currency)
      return t('minimumVoucherOrderAmount', {
        amount: localizeCurrency(lang, { amount: voucherMinSubtotal, currency: currency.title_en }),
      });
    if (isOnlinePaymentWithManualOrder) return t('sendCartLink');
    if (actionButtonText) return actionButtonText;
    return placeOrderText;
  };

  const shouldDisableCheckout =
    !cartItems?.length ||
    isCartItemUnavailable ||
    isDeliveryInvalid ||
    isCarPickupInvalid ||
    isMinOrderInValid ||
    (!fulfillmentTimeType && !actionButtonText) ||
    isVoucherMinAmountInValid ||
    !selectedPaymentMethod ||
    (mode === FULFILLMENT_MODES.BEACH && !persistedUmbrellaData?.umbrellaNumber);

  const isCheckoutLoading =
    isCalculating ||
    isSavingCard ||
    isCheckingOut ||
    isCartDetailsLoading ||
    isLoadingShowAddress ||
    (isDeliveryInvalid && !!persistedUserAddressId) ||
    isRemovingCartItemsLoading ||
    (useDeliveryzonesMs && isDelivery && !isManualOrder && checkingSupportedZone);

  const shouldLogError = shouldDisableCheckout && !isCheckoutLoading && getPlaceOrderButtonText() === placeOrderText;

  const handleOnCheckout = useCallback(
    async (paymentToken?: string) => {
      const { openAt, closeAt } = getOpenAndCloseHours(supportedDeliveryZone, branchDetails, isDelivery);
      const isTimeNowLaterThanEndTime = checkIfTimeNowInBetweenAsapTimeUsingDzms({ openAt, closeAt }, useDeliveryzonesMs);

      if (shouldDisableCheckout) return;
      if (
        isDisabledUntilOrderTimeIsChosen ||
        (!isTimeNowLaterThanEndTime && fulfillmentTimeType === ORDER_TIME_TYPES.ASAP && useDeliveryzonesMs)
      ) {
        setIsCheckingOut(false);
        changeRoute({ pathname: URLS.ESTIMATED_TIME });
        await recalculate?.();
        return;
      }

      const slotStartISO = isOrderScheduled && scheduleSlotInterval?.start.toUTC().toISO();
      const slotEndISO = isOrderScheduled && scheduleSlotInterval?.end.toUTC().toISO();
      const tapPaymentToken = paymentToken || paymentCard?.id;

      const { id: paymentMethodId, name: paymentMethodName } = selectedPaymentMethod || {};

      const checkoutVars = {
        isScheduled: isOrderScheduled,
        roundingAmount: total,
        ...(useDeliveryzonesMs &&
          isDelivery && {
            deliveryZone: {
              ...supportedDeliveryZone,
              countryId: supportedDeliveryZone?.countryId ?? countryId,
              branchId: supportedDeliveryZone?.branchReferenceId?.toString(),
              branchReferenceId: undefined,
              restaurantId: supportedDeliveryZone?.restaurantReferenceId?.toString(),
              restaurantReferenceId: undefined,
              geoShape: JSON.stringify(supportedDeliveryZone?.geoShape),
            },
          }),
        addressId: isDelivery ? userAddress?.id : null,
        expectedAt: slotStartISO || null,
        language: lang === 'ar' ? 1 : 0,
        isWalletUsed: !!isWalletUsed,
        timeSlot: slotStartISO && slotEndISO ? `${slotStartISO},${slotEndISO}` : null,
        paymentType: isWalletAndCash
          ? PAYMENT_METHODS.OFFLINE
          : isWalletAndCreditCard || (paymentMethodId && !isWalletBalanceCoverOrderAmount)
          ? PAYMENT_METHODS.ONLINE
          : PAYMENT_METHODS.OFFLINE,
        ...(((paymentMethodId && !isWalletBalanceCoverOrderAmount) || isWalletAndCreditCard) && {
          paymentGatewayId: paymentData?.paymentGateway?.staticId.toString(),
          paymentMethodId,
        }),
        ...(paymentMethodId && tapPaymentToken && { paymentToken: tapPaymentToken }),
        ...((!paymentMethodId || isWalletBalanceCoverOrderAmount || isWalletAndCash || isWalletAndCreditCard) && {
          paymentOption: isWalletAndCash
            ? PAYMENT_METHODS.WALLET_AND_CASH
            : isWalletAndCreditCard
            ? PAYMENT_METHODS.WALLET_AND_CREDIT_CARD
            : isWalletBalanceCoverOrderAmount
            ? PAYMENT_METHODS.WALLET
            : paymentMethodName,
        }),
        carId: isCarPickup ? persistedUserCarId : null,
        ...(persistedRecipientInfo && { isGift: !!persistedRecipientInfo, recipient: persistedRecipientInfo }),
        isManualOrder: isManualOrder || !!hasReferenceId,
        ...(operatorId && { operatorId }),
        ...(mode === FULFILLMENT_MODES.BEACH && {
          ...persistedUmbrellaData,
        }),
      };

      const sendCartLinkVariables = {
        selectedOrderMode: mode,
        fulfillmentTimeType: persistedFulfillmentTimeAndType,
        cartId: cartData.cartId,
        ...(persistedUserCarId && { selectedUserCarId: persistedUserCarId }),
        ...(area && { selectedArea: { id: area.id } }),
        ...(selectedCoords && { selectedCoordinates: selectedCoords }),
        ...(supportedDeliveryZone && { supportedDeliveryZone }),
        selectedPaymentMethod,
        operatorId,
        ...(mode === FULFILLMENT_MODES.BEACH && {
          ...persistedUmbrellaData,
        }),
      };

      if (isOnlinePaymentWithManualOrder) await sendCartLink(sendCartLinkVariables);
      else await checkout(checkoutVars);

      setIsCheckingOut(false);
    },
    [
      checkout,
      sendCartLink,
      total,
      isDelivery,
      isWalletUsed,
      isOrderScheduled,
      paymentCard,
      paymentData,
      selectedPaymentMethod,
      scheduleSlotInterval,
      shouldDisableCheckout,
      userAddress?.id,
      isCarPickup,
      persistedUserCarId,
      isOnlinePaymentWithManualOrder,
      isScheduledEnabled,
      fulfillmentTimeType,
      supportedDeliveryZone,
    ],
  );

  useEffect(() => {
    if (userInfo) setUserInfoCookies(userInfo?.phoneNumber);
    if (cartItems && cartItems.length >= 1) {
      trackFacebookEvent(
        FACEBOOK_PIXEL_EVENTS.INITIATE_CHECKOUT,
        {
          num_items: cartItems.reduce((totalNum, item) => totalNum + item.variantQuantity, 0),
          currency: currency.title_en,
          content_type: DATA_LAYER_CONTENT_TYPE.PRODUCT,
          value: total || 0,

          contents: cartItems.map(singleCartItem => ({
            id: singleCartItem.menuItem.uuid,
            item_price: singleCartItem.unitPrice || 0,
            title: singleCartItem.menuItem.titleEn,
            quantity: singleCartItem.variantQuantity,
          })),
        },
        userDataBackEnd,
        createCapiEvent,
        capiEnabled,
      );
      trackSnapchatEvents(SNAPCHAT_EVENTS.INITIATE_CHECKOUT, {
        currency: currency.title_en,
        price: total || 0,
        item_ids: cartItems.map(signleCartItem => signleCartItem.menuItem.uuid),
        number_items: cartItems.reduce((totalNum, item) => totalNum + item.variantQuantity, 0),
      });
      trackTiktokEvents(
        TIKTOK_EVENTS.INITIATE_CHECKOUT,
        {
          contents: cartItems.map(signleCartItem => ({
            content_id: signleCartItem.menuItem.uuid,
            price: signleCartItem.unitPrice || 0,
            content_name: signleCartItem.menuItem.titleEn,
            quantity: signleCartItem.variantQuantity,
          })),
          value: total || 0,
          currency: currency.title_en,
        },
        tiktokData,
      );
    }
  }, [cartItems && cartItems.length === 0]);

  useLayoutEffect(() => {
    if (!cartData?.cartId && !hasReferenceId) {
      replaceRoute({ pathname: URLS.HOME }, { locale: lang });
    }
    CustomActionsRegistry.setCustomAction({ key: CustomActionsEnum.MutateCartDetails, fn: mutateCartDetails });
    CustomActionsRegistry.setCustomAction({ key: CustomActionsEnum.RemoveCartItemsOutOfSync, fn: removeItemsOutOfSync });
    CustomActionsRegistry.setCustomAction({
      key: CustomActionsEnum.UpdatePaymentMethods,
      fn: () => {
        removePaymentCard();
        mutatePaymentMethods();
      },
    });
    CustomActionsRegistry.setCustomAction({
      key: CustomActionsEnum.RedirectToEditAddress,
      fn: () => {
        replaceRoute({ pathname: URLS.EDIT_ADDRESS, query: { id: userAddress?.id } });
      },
    });
  }, []);

  useEffect(() => {
    const isCarPickupAndNoCarSelected = isCarPickup && !persistedUserCarId;
    const isDeliveryAndNoAddressApplicableOrSelected =
      isDelivery &&
      !useDeliveryzonesMs &&
      !(persistedUserAddressId || (userBookData as AddressBook)?.recentlyUsedAddressPerArea);

    if ((userBook?.length && isCarPickupAndNoCarSelected) || isDeliveryAndNoAddressApplicableOrSelected)
      setShowActionSheet(true);
  }, [userBook, userBookData]);

  useEffect(() => {
    if (!isCartDetailsLoading && !cartItemsCount && !hasReferenceId) replaceRoute({ pathname: URLS.SHOPPING_CART });
  }, [cartItemsCount, isCartDetailsLoading]);

  useEffect(() => {
    if (shouldLogError) {
      logger.error({
        error: '🚀 Should disable checkout btn',
        extraData: {
          phoneNumber: userInfo?.phoneNumber,
          cartItemsCount: cartItems.length,
          isCartItemUnavailable,
          isDeliveryInvalid,
          isCarPickupInvalid,
          isMinOrderInValid,
          fulfillmentTimeTypeAndActionText: !fulfillmentTimeType && !actionButtonText,
          fulfillmentTimeType,
          actionButtonText,
          isVoucherMinAmountInValid,
          selectedPaymentMethod,
          modeType: mode,
          isScheduledEnabled,
          estimatedTimeText,
          scheduleInfo,
          scheduleSlotInterval,
        },
      });
    }
  }, [shouldLogError]);

  const doubleCheckSupportedZone = useCallback(
    async (isCalledThroughPlaceOrder: boolean) => {
      setCheckingSupportedZone(true);
      const supportedZoneResponse = await checkSupportedZoneCallback({
        restaurantReferenceId: storeId,
        geoPoint: [parseFloat(userAddress?.lng), parseFloat(userAddress?.lat)],
      });
      if (!supportedZoneResponse?.hasError) {
        const { supportedDeliveryZone: supportedDeliveryZoneTemp } = supportedZoneResponse;
        if (
          compareDeliveryZoneInCartCacheResponse(supportedDeliveryZone, supportedDeliveryZoneTemp, cartDetails?.deliveryZone)
        ) {
          if (isCalledThroughPlaceOrder) handleOnCheckout();
          setCheckingSupportedZone(false);
        } else {
          await setSupportedDeliveryZone(supportedDeliveryZoneTemp as DeliveryZoneMs);
          const updateCartVars = {
            branchId: supportedDeliveryZoneTemp?.branchReferenceId?.toString(),
            deliveryZone: {
              ...(supportedDeliveryZoneTemp as DeliveryZoneMs),
              branchId: supportedDeliveryZoneTemp?.branchReferenceId?.toString(),
              branchReferenceId: undefined,
              restaurantId: supportedDeliveryZoneTemp?.restaurantReferenceId?.toString(),
              restaurantReferenceId: undefined,
              geoShape: JSON.stringify(supportedDeliveryZoneTemp?.geoShape),
              userLat: userAddress?.lat ? parseFloat(userAddress.lat) : selectedCoords?.lat,
              userLng: userAddress?.lng ? parseFloat(userAddress.lng) : selectedCoords?.lng,
            },
            deliveryType: FULFILLMENT_MODES.DELIVERY,
            ...(persistedUserAddressId && {
              addressId: userAddress?.id || persistedUserAddressId,
            }),
          };
          await updateCart(updateCartVars);
          setIsCheckingOut(false);
          setCheckingSupportedZone(false);
          if (isCalledThroughPlaceOrder) {
            setHasDeliveryZoneChanged(true);
            setShowActionSheet(true);
          }
        }
      } else {
        await mutateUserBook();
        await mutateShowAddress();
        setIsCheckingOut(false);
        setCheckingSupportedZone(false);
      }
    },
    [
      cartDetails?.deliveryZone,
      checkSupportedZoneCallback,
      handleOnCheckout,
      mutateShowAddress,
      mutateUserBook,
      persistedUserAddressId,
      selectedCoords?.lat,
      selectedCoords?.lng,
      setSupportedDeliveryZone,
      storeId,
      supportedDeliveryZone,
      updateCart,
      userAddress?.id,
      userAddress?.lat,
      userAddress?.lng,
    ],
  );

  useEffect(() => {
    if (isDelivery && userAddress?.lat && userAddress?.lng && useDeliveryzonesMs && !isManualOrder)
      doubleCheckSupportedZone(false);
  }, [userAddress?.lat, userAddress?.lng]);

  const onBackClick = () => {
    changeRoute({
      pathname: URLS.SHOPPING_CART,
    });
  };

  const actionSheetHeaderTitle = () => {
    if (hasDeliveryZoneChanged) return t('deliveryInfoUpdated');
    if (isDelivery) return `${t('address:select')} ${t('address:address')}`;
    return `${t('address:select')} ${t('car')}`;
  };

  const userAddressMapCenter: CoordsType = { lat: parseFloat(userAddress?.lat), lng: parseFloat(userAddress?.lng) };

  if (isPaymentLoading || isFulfillmentSettingsLoading) return <CheckoutLoadingComponent />;

  return (
    <StyledPageWrapper>
      {/* TODO: check back should redirect to order fast or cart or homepage */}
      <StoreFrontHeaderComponent title={t('checkout')} onBack={onBackClick} />
      {useDeliveryzonesMs && (
        <StyledEditLocationContainer>
          <StyledMapWrapper>
            <OpenStreetMap
              center={mode === FULFILLMENT_MODES.DELIVERY ? selectedCoords || userAddressMapCenter : branchCoordinates}
              isStatic
              zoom={16}
              hasAutoComplete={false}
              hasCurrLocationButton={false}
              hasZoomControl={false}
            />
          </StyledMapWrapper>
        </StyledEditLocationContainer>
      )}
      {(isCarPickup || isDelivery) && (
        <StyledActionSheetWrapper>
          <ActionSheet headerTitle={actionSheetHeaderTitle()} isOpen={showActionSheet} setIsOpen={setShowActionSheet}>
            {hasDeliveryZoneChanged ? (
              <StyledDeliveryInfoUpdatedContainer>
                <Typography variant="heading16" marginBottom={theme.space[4]}>
                  {t('deliveryInfoUpdatedDetailed')}
                </Typography>
                <Button
                  text={t('continue')}
                  variant="primary"
                  onClick={() => {
                    setShowActionSheet(false);
                    setHasDeliveryZoneChanged(false);
                  }}
                  isFluid
                />
              </StyledDeliveryInfoUpdatedContainer>
            ) : (
              <UserBookList showAddLink={false} isDelivery={isDelivery} onItemClick={() => setShowActionSheet(false)} />
            )}
          </ActionSheet>
        </StyledActionSheetWrapper>
      )}
      <CheckoutUserInfoComponent
        estimatedTimeDetails={estimatedTimeDetails}
        onChange={() => setShowActionSheet(true)}
        actionButtonText={actionButtonText}
      />
      {isDelivery && setting?.sendAsGiftEnabled && <GiftOrderingSection />}
      <ReceiptComponent
        currency={currency}
        subTotal={subtotal}
        discountedAmount={discountedAmount}
        voucherAmount={voucherAmount}
        tax={tax}
        taxInclusive={taxInclusive}
        taxPercentage={taxPercentage}
        deliveryFee={deliveryFee}
        serviceFee={serviceFee}
        total={total}
        selectedPaymentMethod={selectedPaymentMethod}
        voucherDiscountedItems={voucherDiscountedItems}
        possibleCashbackAmount={!isWalletBalanceCoverOrderAmount && possibleCashbackAmount}
        deliveryVatInclusive={deliveryVatInclusive}
      />
      <PaymentMethodsSection
        isWalletBalanceCoverOrderAmount={isWalletBalanceCoverOrderAmount}
        handleTapCardSubmitted={handleOnCheckout}
        setIsSavingCard={setIsSavingCard}
        selectedMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
      />
      <StyledButtonWrapper>
        <Button
          text={getPlaceOrderButtonText()}
          isFluid
          isDisabled={shouldDisableCheckout || isCartDetailsLoading || isCheckoutLoading}
          onClick={async () => {
            setIsCheckingOut(true);
            const logEvents = {
              message: `PurchaseClicked -> storeId: ${storeId}, userData: ${JSON.stringify(
                userInfo?.phoneNumber,
              )}, fbp:${Cookies.get('_fbp')}, fbc:${Cookies.get('_fbc')},browser:${window?.navigator?.userAgent},
                fbqScript: ${window?.fbq}
                `,
            };
            logCloudWatch(logEvents);
            if (
              (paymentCard?.id && selectedPaymentMethod.name === PAYMENT_METHODS.CREDIT_CARD) ||
              selectedPaymentMethod.name !== PAYMENT_METHODS.CREDIT_CARD ||
              paymentData?.paymentGateway?.staticId !== PAYMENT_GATEWAYS.TAP ||
              isManualOrder ||
              isWalletBalanceCoverOrderAmount
            ) {
              if (useDeliveryzonesMs && isDelivery && !isManualOrder) {
                doubleCheckSupportedZone(true);
              } else handleOnCheckout();
            }
          }}
          type="submit"
          form={selectedPaymentMethod?.name === PAYMENT_METHODS.CREDIT_CARD ? 'tap-form-container' : ''}
          isLoading={isCheckoutLoading}
        />
      </StyledButtonWrapper>
    </StyledPageWrapper>
  );
};

export default CheckoutPage;
